import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw, ShieldAlert, Trash2, UserCog } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const STATE_LABEL = {
  active: "Active", draft: "Draft", archived: "Archived", suspended: "Suspended",
  pending_deletion: "Pending deletion", deleted: "Purged",
};

export const stateVariant = (s) =>
  s === "suspended" || s === "pending_deletion" ? "destructive"
    : s === "active" ? "default" : "secondary";

export function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function Row({ label, children, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-[560]" data-testid={testId}>{children}</span>
    </div>
  );
}

/** Platform-staff detail + actions for one Group. Every action reuses an existing
 *  /api/admin/groups endpoint and its authorization; this adds no permission system. */
export function AdminGroupDetail({ groupId, onClose }) {
  const qc = useQueryClient();
  const [tab, setTab] = React.useState("overview");
  const [reason, setReason] = React.useState("");
  const [newOwner, setNewOwner] = React.useState("");
  const [ackOwner, setAckOwner] = React.useState(false);
  const [typed, setTyped] = React.useState("");
  const [busy, setBusy] = React.useState("");

  const detail = useQuery({
    queryKey: ["admin-group", groupId],
    queryFn: async () => (await api.get(`/admin/groups/${groupId}`)).data,
    enabled: !!groupId,
  });
  const impact = useQuery({
    queryKey: ["admin-group-impact", groupId],
    queryFn: async () => (await api.get(`/admin/groups/${groupId}/purge/preview`)).data,
    enabled: !!groupId,
  });
  const members = useQuery({
    queryKey: ["admin-group-members", groupId],
    queryFn: async () => (await api.get(`/admin/groups/${groupId}/members`)).data,
    enabled: !!groupId && tab === "ownership",
  });
  const audit = useQuery({
    queryKey: ["admin-group-audit", groupId],
    queryFn: async () => (await api.get(`/admin/groups/${groupId}/audit?limit=50`)).data,
    enabled: !!groupId && tab === "audit",
  });

  const g = detail.data;
  const imp = impact.data;

  const act = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      toast.success(`${label} done.`);
      qc.invalidateQueries({ queryKey: ["admin-groups"] });
      detail.refetch();
      impact.refetch();
      audit.refetch();
      setTyped("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `${label} failed.`);
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={!!groupId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="admin-group-detail">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {g?.display_name || "Group"}
            {g ? <Badge variant={stateVariant(g.state)} data-testid="admin-group-detail-state">{STATE_LABEL[g.state] || g.state}</Badge> : null}
          </DialogTitle>
          <DialogDescription>
            {g?.slug ? `@${g.slug}` : "No handle"} · platform administration
          </DialogDescription>
        </DialogHeader>

        {detail.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}

        {g ? (
          <>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="admin-group-tabs">
                <TabsTrigger value="overview" data-testid="admin-group-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="enforcement" data-testid="admin-group-tab-enforcement">Enforcement</TabsTrigger>
                <TabsTrigger value="ownership" data-testid="admin-group-tab-ownership">Ownership</TabsTrigger>
                <TabsTrigger value="purge" data-testid="admin-group-tab-purge">Deletion</TabsTrigger>
                <TabsTrigger value="audit" data-testid="admin-group-tab-audit">Audit</TabsTrigger>
              </TabsList>
            </Tabs>

            {tab === "overview" ? (
              <div className="mt-3" data-testid="admin-group-overview">
                <Row label="Members" testId="admin-group-members-count">{g.member_count ?? 0}</Row>
                <Row label="Owner">{g.owner_user_id || "—"}</Row>
                <Row label="Moderation hold">{g.moderation_hold ? "Yes" : "No"}</Row>
                <Row label="Suspended at">{fmt(g.suspended_at)}</Row>
                <Row label="Deletion requested">{fmt(g.deletion_requested_at)}</Row>
                <Row label="Permanent deletion" testId="admin-group-purge-at">{fmt(g.purge_at)}</Row>
                <Row label="Purged at">{fmt(g.purged_at)}</Row>
                <Row label="Needs ownership recovery">{g.ownership_recovery_required ? "Yes" : "No"}</Row>
              </div>
            ) : null}

            {tab === "enforcement" ? (
              <div className="mt-3 space-y-3" data-testid="admin-group-enforcement">
                <Row label="Review requested">{fmt(g.review_requested_at)}</Row>
                <Row label="Suspension reason">{g.suspension_reason || "—"}</Row>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                          placeholder="Reason (recorded in the platform audit log)"
                          data-testid="admin-group-reason" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="destructive" disabled={!!busy || g.state === "suspended" || g.state === "deleted"}
                          data-testid="admin-group-suspend"
                          onClick={() => act("Suspend", () => api.post(`/admin/groups/${groupId}/suspend`, { reason, member_message: reason }))}>
                    <ShieldAlert className="mr-2 h-4 w-4" />Suspend
                  </Button>
                  <Button size="sm" variant="outline" disabled={!!busy || g.state !== "suspended"}
                          data-testid="admin-group-reinstate"
                          onClick={() => act("Reinstate", () => api.post(`/admin/groups/${groupId}/reinstate`, { reason }))}>
                    <RotateCcw className="mr-2 h-4 w-4" />Reinstate
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Reinstating returns the group to the state it was in before suspension and keeps
                  any original deletion deadline.
                </p>
              </div>
            ) : null}

            {tab === "ownership" ? (
              <div className="mt-3 space-y-3" data-testid="admin-group-ownership">
                <p className="text-sm text-muted-foreground">
                  Hand the group to an existing <strong>active member</strong> — for an abandoned
                  group or one whose owner deleted their account. Recorded on the group and
                  platform audit logs.
                </p>
                <Select value={newOwner} onValueChange={setNewOwner}>
                  <SelectTrigger data-testid="admin-group-owner-select">
                    <SelectValue placeholder={members.isLoading ? "Loading members…" : "Choose a member"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(members.data?.items || []).map((mm) => (
                      <SelectItem key={mm.user_id} value={mm.user_id}>
                        {mm.display_name} · {mm.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex cursor-pointer items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
                  <input type="checkbox" className="mt-1" checked={ackOwner}
                         data-testid="admin-group-owner-ack"
                         onChange={(e) => setAckOwner(e.target.checked)} />
                  <span>
                    I confirm this platform-level ownership transfer. The current owner becomes
                    an admin and this action is recorded.
                  </span>
                </label>
                <Button size="sm" disabled={!newOwner || !ackOwner || !!busy} data-testid="admin-group-recover-ownership"
                        onClick={() => act("Ownership recovery", () => api.post(`/admin/groups/${groupId}/recover-ownership`, { user_id: newOwner, reason }))}>
                  <UserCog className="mr-2 h-4 w-4" />Transfer ownership
                </Button>
              </div>
            ) : null}

            {tab === "purge" ? (
              <div className="mt-3 space-y-3" data-testid="admin-group-purge">
                <Row label="Eligible for purge" testId="admin-group-eligible">{imp?.eligible ? "Yes" : "No"}</Row>
                <Row label="Window expired">{imp?.window_expired ? "Yes" : "No"}</Row>
                <Row label="Enforcement hold" testId="admin-group-hold">{imp?.hold ? imp.hold.detail : "None"}</Row>
                {imp ? (
                  <div className="rounded-md border border-border p-3 text-xs" data-testid="admin-group-impact">
                    <p className="font-[640] text-destructive">Permanently destroys</p>
                    <ul className="mt-1 grid grid-cols-2 gap-x-4">
                      {Object.entries(imp.destroys || {}).map(([k, v]) => (
                        <li key={k} className="flex justify-between"><span className="text-muted-foreground">{k.replace(/_/g, " ")}</span><span>{v}</span></li>
                      ))}
                    </ul>
                    <p className="mt-3 font-[640]">Retained (platform records)</p>
                    <ul className="mt-1 grid grid-cols-2 gap-x-4">
                      {Object.entries(imp.retains || {}).map(([k, v]) => (
                        <li key={k} className="flex justify-between"><span className="text-muted-foreground">{k.replace(/_/g, " ")}</span><span>{v}</span></li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {imp?.progress && Object.keys(imp.progress).length ? (
                  <div className="rounded-md bg-secondary/40 p-3 text-xs" data-testid="admin-group-purge-progress">
                    <p className="font-[640]">Purge progress (resumable)</p>
                    {Object.entries(imp.progress).map(([step, v]) => (
                      <div key={step} className="flex justify-between"><span className="text-muted-foreground">{step}</span><span>{v?.count ?? 0} · {fmt(v?.at)}</span></div>
                    ))}
                  </div>
                ) : null}
                {(imp?.failures || []).length ? (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs" data-testid="admin-group-purge-failures">
                    <p className="font-[640] text-destructive">Failed steps (retried on the next run)</p>
                    {imp.failures.map((f, i) => <div key={i}>{f.step}: {f.error}</div>)}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={!!busy || g.state !== "pending_deletion"}
                          data-testid="admin-group-cancel-deletion"
                          onClick={() => act("Cancel deletion", () => api.post(`/admin/groups/${groupId}/cancel-deletion`, { reason }))}>
                    Cancel deletion
                  </Button>
                </div>
                {g.state === "pending_deletion" ? (
                  <div className="rounded-md border border-destructive/40 p-3">
                    <p className="flex items-center gap-2 text-sm font-[640] text-destructive">
                      <AlertTriangle className="h-4 w-4" />Irreversible
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Type <strong>PURGE</strong> to confirm. Blocked while an enforcement hold
                      exists or the 30-day window has not expired.
                    </p>
                    <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-2"
                           placeholder="PURGE" data-testid="admin-group-purge-typed" />
                    <Button size="sm" variant="destructive" className="mt-2"
                            disabled={typed.trim() !== "PURGE" || !!busy || !imp?.eligible}
                            data-testid="admin-group-purge-confirm"
                            onClick={() => act("Purge", () => api.post(`/admin/groups/${groupId}/purge`, {}))}>
                      {busy === "Purge" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                      Purge permanently
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {tab === "audit" ? (
              <div className="mt-3 space-y-1 text-xs" data-testid="admin-group-audit">
                {(audit.data?.entries || []).length === 0 ? (
                  <p className="text-muted-foreground">No audit entries.</p>
                ) : (audit.data.entries.map((e, i) => (
                  <div key={i} className="flex justify-between gap-3 border-b border-border/50 py-1.5">
                    <span className="font-[560]">{e.action}</span>
                    <span className="text-muted-foreground">{e.actor_user_id || "system"} · {fmt(e.created_at)}</span>
                  </div>
                )))}
              </div>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
